/* eslint-env browser */
import compact from 'lodash/compact'
import last from 'lodash/last'
import merge from 'lodash/merge'
import defaultsDeep from 'lodash/defaultsDeep'
import isEmpty from 'lodash/isEmpty'
import queryString from 'query-string'
import { isPreviewMode } from './slices/content'
import { getCurrentQueryString } from './slices/location'

import {
  getContentApiUrl,
  getGlobalContentApiUrl,
  getPreviewApiUrl,
  getPagesApiUrl,
  getShopApiUrl
} from './slices/config'
import {
  getCartId,
  setCartError,
  clearCartError,
  setCartBusy
} from './slices/cart'
import { globalContentActionCreator } from './slices/content/global'
import { pageContentActionCreator } from './slices/content/page'
import { pagesContentActionCreator } from './slices/content/pages'
import { cartActionCreator, updateCartFromShopify } from './actions/cart'

const toUrl = (components, query) => compact(components).join('/') + (isEmpty(query) ? '' : `?${queryString.stringify(query)}`)

/**
 * The last parameter can optionally be an options object. If a `qs` option is specified in the options object it will
 * be converted to the query string in the url
 */
export async function fetchJson(...components) {
  const options = {
    headers: {
      Accept: 'application/json'
    },
    credentials: 'omit'
  }
  if (typeof last(components) === 'object') {
    merge(options, components.pop())
  }

  const { qs, json, ...fetchOptions } = options
  if (json) {
    defaultsDeep(fetchOptions, {
      headers: {
        'content-type': 'application/json'
      },
      method: 'post',
      body: JSON.stringify(json)
    })
  }

  const response = await fetch(toUrl(components, qs), fetchOptions)
  const data = await response.json()

  if (!response.ok) {
    const error = new Error((data.error && data.error.message) || 'Failed to fetch content')
    error.status = response.status
    throw error
  }

  return data
}

export async function sendFetchPageContentRequest(dispatch, getState, {
  path, qs, actionCreator = pageContentActionCreator
} = {}) {
  const queryString = getCurrentQueryString(getState())
  const apiUrlSelector = isPreviewMode(getState()) ? getPreviewApiUrl : getContentApiUrl
  const components = [apiUrlSelector(getState())]
  const pathComponents = compact(path.split('/'))
  const options = { qs: { ...queryString, ...qs } }
  components.push(path === '/' ? 'home' : last(pathComponents))
  return dispatch(actionCreator(fetchJson(...components, options)))
}

export async function fetchPageContent(dispatch, getState, path, actionMeta) {
  return sendFetchPageContentRequest(dispatch, getState, {
    path,
    actionCreator: (promise) => merge(pageContentActionCreator(promise), { meta: actionMeta })
  })
}

export async function fetchGlobalContent(dispatch, getState) {
  const api = getGlobalContentApiUrl(getState())
  return dispatch(globalContentActionCreator(fetchJson(api)))
}

export async function fetchPageNotFoundContent(dispatch, getState) {
  const api = getGlobalContentApiUrl(getState())
  return dispatch(pageContentActionCreator(fetchJson(api, 'error-page-404')))
}

export async function fetchPages(dispatch, getState, sliceId, qs, loadMore) {
  const state = getState()
  const api = getPagesApiUrl(state)
  return dispatch(pagesContentActionCreator(fetchJson(api, { qs }), loadMore))
}

async function sendCartRequest(dispatch, getState, { cartId, ...options } = {}) {
  const state = getState()
  const api = getShopApiUrl(state)
  const components = [api, 'checkout']
  if (!cartId && options.method !== 'post') {
    cartId = getCartId(state)
  }
  if (cartId) {
    components.push(encodeURIComponent(cartId))
  }
  return dispatch(cartActionCreator(fetchJson(...components, options)))
}

export async function fetchCart(dispatch, getState, cartId) {
  return sendCartRequest(dispatch, getState, { cartId })
}

export async function sendCreateCart(dispatch, getState, lineItems) {
  const json = { lineItems }
  return sendCartRequest(dispatch, getState, { method: 'post', json })
}

export async function sendUpdateCartLineItems(dispatch, getState, lineItems) {
  const json = { lineItems }
  return sendCartRequest(dispatch, getState, { method: 'put', json })
}

// NEW SHOPIFY CART INTEGRATION FUNCTIONS

/**
 * Convert a Shopify variant ID to the format needed for API
 * @param {string} variantId - The raw variant ID
 * @returns {string} - Properly formatted variant ID for the Shopify API
 */
export const encodeVariantId = (variantId) => {
  if (!variantId) return null

  // If it's already in gid:// format, return as is
  if (typeof variantId === 'string' && variantId.startsWith('gid://')) {
    return variantId
  }

  // If it's already base64 encoded (starts with Z2lk), return as is
  if (typeof variantId === 'string' && variantId.startsWith('Z2lk')) {
    return variantId
  }

  // Normalize the variant ID by stripping any "gid://" prefixes
  let normalizedId = variantId
  if (typeof normalizedId === 'string') {
    // Extract just the ID part if it's in a URL format
    const parts = normalizedId.split('/')
    normalizedId = parts[parts.length - 1]

    // Remove any non-numeric characters
    normalizedId = normalizedId.replace(/\D/g, '')
  }

  // Format to gid://shopify/ProductVariant/ID as expected by server.js
  return `gid://shopify/ProductVariant/${normalizedId}`
}

/**
 * Convert a Shopify selling plan ID to the format needed for API
 * @param {string|null} sellingPlanId - The raw selling plan ID, if any
 * @returns {string|null} - Properly formatted selling plan ID for the Shopify API, or null
 */
export const encodeSellingPlanId = (sellingPlanId) => {
  if (!sellingPlanId) return null

  // If it's already in gid:// format, return as is
  if (typeof sellingPlanId === 'string' && sellingPlanId.startsWith('gid://')) {
    return sellingPlanId
  }

  // If it's already base64 encoded (starts with Z2lk), return as is
  if (typeof sellingPlanId === 'string' && sellingPlanId.startsWith('Z2lk')) {
    return sellingPlanId
  }

  // Normalize the ID by removing any non-numeric characters
  let normalizedId = sellingPlanId
  if (typeof normalizedId === 'string') {
    // Extract just the ID part if it's in a URL format
    const parts = normalizedId.split('/')
    normalizedId = parts[parts.length - 1]

    // Remove any non-numeric characters
    normalizedId = normalizedId.replace(/\D/g, '')
  }

  // Format to gid://shopify/SellingPlan/ID as expected by server.js
  return `gid://shopify/SellingPlan/${normalizedId}`
}

// Helper function to directly fetch Shopify prices for a specific product
export async function fetchProductPriceFromShopify(productId) {
  try {
    const response = await fetch(`https://api-server.discoveredfoods.com/api/getProductById?id=${productId}`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' }
    })

    if (!response.ok) {
      console.error(`Failed to fetch product prices, status: ${response.status}`)
      return null
    }

    const data = await response.json()
    if (data && data.data && data.data.product) {
      const product = data.data.product
      const variant = product.variants.edges[0]?.node

      if (variant && variant.price) {
        // console.log(`Successfully fetched price for product ${productId}: ${variant.price.amount}`)
        return {
          price: variant.price.amount,
          comparePrice: variant.compareAtPrice?.amount || null,
          title: product.title,
          handle: product.handle
        }
      }
    }
    return null
  } catch (error) {
    console.error('Error fetching product price:', error)
    return null
  }
}

// Function to convert variant ID to product ID
export function variantIdToProductId(variantId) {
  if (!variantId) return null
  // Handle different formats of variantID
  const parts = variantId.toString().split('/')
  const lastPart = parts[parts.length - 1]
  const productId = lastPart.split('-')[0] // Extract product ID from variant ID
  return productId
}

/**
 * Helper function to get product handle from variant ID
 * Searches for BuyButton elements in the DOM to find matching product handle
 * @param {string} variantId - The variant ID to look up
 * @returns {string|null} - The product handle or null if not found
 */
export const getProductHandleFromVariantId = (variantId) => {
  if (!variantId) return null

  // Normalize the variant ID
  let normalizedId = variantId
  if (typeof normalizedId === 'string') {
    const parts = normalizedId.split('/')
    normalizedId = parts[parts.length - 1]
    normalizedId = normalizedId.replace(/\D/g, '')
  }

  // Try to find a BuyButton with this variant ID
  if (typeof document !== 'undefined') {
    const buyButtons = document.querySelectorAll('.BuyButton')
    for (const button of buyButtons) {
      // Check data attributes
      const buttonVariantId = button.getAttribute('data-variant-id')
      if (buttonVariantId) {
        // Normalize the button variant ID for comparison
        const normalizedButtonId = buttonVariantId.toString().split('/').pop().replace(/\D/g, '')
        if (normalizedButtonId === normalizedId.toString()) {
          return button.getAttribute('data-product-handle')
        }
      }
    }
  }

  // Return a simple handle based on variant ID if nothing else works
  return `product-${normalizedId}`
}

/**
 * Helper function to check for Shopify-specific errors in response data
 * @param {Object} data - Response data from Shopify API
 * @returns {Object|null} - Error object if found, null otherwise
 */
export const checkForShopifyErrors = (data) => {
  if (!data) return null

  // Function to check if error is related to an invalid cart
  const checkInvalidCart = (message) => {
    if (!message) return false
    const lowerMsg = message.toLowerCase()
    return lowerMsg.includes('cart does not exist') ||
      lowerMsg.includes('cart is invalid') ||
      lowerMsg.includes('invalid cart')
  }

  // Check for server.js specific error format first
  if (data.errors && Array.isArray(data.errors) && data.errors.length > 0) {
    const message = data.errors.map(e => e.message).join(', ')
    return {
      message,
      isInvalidCart: checkInvalidCart(message)
    }
  }

  // Check for cartLinesAdd errors
  if (data.data?.cartLinesAdd?.userErrors && data.data.cartLinesAdd.userErrors.length > 0) {
    const message = data.data.cartLinesAdd.userErrors.map(e => e.message).join(', ')
    return {
      message,
      isInvalidCart: checkInvalidCart(message)
    }
  }

  // Check for cartCreate errors
  if (data.data?.cartCreate?.userErrors && data.data.cartCreate.userErrors.length > 0) {
    const message = data.data.cartCreate.userErrors.map(e => e.message).join(', ')
    return {
      message,
      isInvalidCart: checkInvalidCart(message)
    }
  }

  // Check for cartLinesUpdate errors
  if (data.data?.cartLinesUpdate?.userErrors && data.data.cartLinesUpdate.userErrors.length > 0) {
    const message = data.data.cartLinesUpdate.userErrors.map(e => e.message).join(', ')
    return {
      message,
      isInvalidCart: checkInvalidCart(message)
    }
  }

  // Check for cartLinesRemove errors
  if (data.data?.cartLinesRemove?.userErrors && data.data.cartLinesRemove.userErrors.length > 0) {
    const message = data.data.cartLinesRemove.userErrors.map(e => e.message).join(', ')
    return {
      message,
      isInvalidCart: checkInvalidCart(message)
    }
  }

  return null
}

// Helper function to process cart response
export async function processCartResponse(response, dispatch) {
  try {
    const data = await response.json()
    // // console.log('🔥 raw GraphQL payload:', data)
    // // console.log('Cart API response:', data)

    // Check for Shopify API errors
    const shopifyError = checkForShopifyErrors(data)
    if (shopifyError) {
      // console.error('Shopify API error:', shopifyError)
      dispatch(setCartError({ message: shopifyError.message }))

      // If it's an invalid cart error, clear localStorage to force a new cart creation next time
      if (shopifyError.isInvalidCart) {
        console.log('🧹 Invalid cart detected from Shopify error. Clearing local storage...')
        window.localStorage.removeItem('cartId')
        window.localStorage.removeItem('cartLines')
        window.localStorage.removeItem('discoveredFoodsCart1')

        // Reset cart state to empty
        dispatch(updateCartFromShopify({
          id: null,
          checkoutUrl: null,
          totalPrice: { amount: '0.00', currencyCode: 'AUD' },
          subtotalPrice: { amount: '0.00', currencyCode: 'AUD' },
          lineItemsSubtotalPrice: { amount: '0.00', currencyCode: 'AUD' },
          lineItems: []
        }))
      }

      return null
    }

    // Extract the cart from API response based on server.js structure
    let cart
    if (data?.data?.cart) {
      cart = data.data.cart
    } else if (data?.data?.cartLinesUpdate?.cart) {
      cart = data.data.cartLinesUpdate.cart
    } else if (data && data.data && data.data.cartLinesAdd && data.data.cartLinesAdd.cart) {
      cart = data.data.cartLinesAdd.cart
    } else if (data && data.data && data.data.cartCreate && data.data.cartCreate.cart) {
      cart = data.data.cartCreate.cart
    } else if (data && data.data && data.data.cartLinesRemove && data.data.cartLinesRemove.cart) {
      cart = data.data.cartLinesRemove.cart
    } else if (data && data.cart) {
      cart = data.cart
    } else {
      // console.error('Unexpected cart data format:', data)
      cart = {
        id: null,
        checkoutUrl: null,
        lines: { edges: [] }
      }
    }
    // // console.log('🛒 normalized cart object:', cart)

    // Extract line items for processing
    const lineItems = cart.lines?.edges || []

    // Get stored cart lines with price information
    // const storedCartLines = JSON.parse(localStorage.getItem('cartLines') || '[]')
    // console.log('Stored cart lines:', storedCartLines)

    // Get stored product prices
    // const storedPrices = JSON.parse(localStorage.getItem('shopifyProductPrices') || '{}')
    // console.log('Stored product prices:', storedPrices)

    // Process items with better price handling based on server.js response structure
    const lineItemsData = lineItems.map((edge, index) => {
      const node = edge.node
      const timestamp = Date.now()
      const randomId = Math.floor(Math.random() * 10000)

      // Get line item ID
      const lineItemId = node.id

      // Extract merchandise data (ProductVariant)
      const merchandise = node.merchandise
      if (!merchandise) {
        console.error('Missing merchandise data for line item:', node)
        return null
      }

      // Get product details - based on server.js GraphQL query structure
      const productTitle = merchandise.product?.title || 'Product'
      const variantTitle = merchandise.title || 'Default Title'
      const productHandle = productTitle.toLowerCase().replace(/\s+/g, '-')

      // Find variant ID using server API structure
      const variantId = merchandise.id?.split('/').pop() || `temp-variant-${index}-${timestamp}-${randomId}`

      // ---------- PRICE DETERMINATION ----------
      // 1) If the line has a cost (i.e. with selling plan applied), use that
      let price = node.cost?.amountPerQuantity?.amount
      let priceSource = price ? 'cost.amountPerQuantity' : null

      // 2) Otherwise fall back to the variant’s list price
      if (!price) {
        price = merchandise.priceV2?.amount
        priceSource = price ? 'priceV2' : 'fallback'
      }

      const edges = cart.lines?.edges || []
      edges.forEach((edge, i) => {
        // // console.log(`--- edge[${i}].node ---`, edge.node)
      })

      // Finally, hard‐default
      if (!price) {
        console.warn('No line‐item price found, defaulting to 0.00')
        price = '0.00'
        priceSource = 'fallback'
      }

      console.log(`Price for ${productTitle} (${priceSource}): ${price}`)

      // Get current quantity
      const quantity = parseInt(node.quantity || 1, 10)

      // Get image - the server.js uses originalSrc instead of url/src
      const imageSrc = merchandise.image?.originalSrc

      // Set up fallback images
      const fallbackImageOptions = [
        // Main product image with handle
        `https://cdn.shopify.com/s/files/1/0552/6549/5309/products/${productHandle}.jpg`,
        `https://cdn.shopify.com/s/files/1/0552/6549/5309/products/${productHandle}.png`,
        `https://cdn.shopify.com/s/files/1/0552/6549/5309/products/${productHandle}_1.jpg`,

        // Try with variant ID if available
        variantId ? `https://cdn.shopify.com/s/files/1/0552/6549/5309/products/variant-${variantId}.jpg` : null,

        // Generic placeholder as last resort
        'https://cdn.shopify.com/s/files/1/0552/6549/5309/files/product-placeholder.png',
        '/assets/product-placeholder.png'
      ].filter(Boolean) // Remove any null values

      const alloc = node.sellingPlanAllocation
      const plan = alloc?.sellingPlan

      const sellingPlan = plan
        ? {
          id: plan.id,
          name: plan.name,
          description: plan.description
        }
        : null

      // Create line item
      return {
        id: lineItemId || `temp-line-${index}-${timestamp}-${randomId}`,
        quantity: quantity,
        sellingPlan,
        variant: {
          id: variantId,
          title: variantTitle,
          price: {
            amount: price,
            currencyCode: merchandise.priceV2?.currencyCode || 'AUD'
          },
          image: {
            src: imageSrc || fallbackImageOptions[0],
            url: imageSrc || fallbackImageOptions[0]
          }
        },
        product: {
          id: `gid://shopify/Product/${productHandle}`,
          title: productTitle,
          handle: productHandle,
          images: [{
            url: imageSrc || fallbackImageOptions[0]
          }],
          featuredImage: {
            url: imageSrc || fallbackImageOptions[0]
          }
        }
      }
    }).filter(Boolean) // Remove any null items

    // Calculate total amount based on line item prices since Shopify might not include discounts
    const totalAmount = lineItemsData.reduce((sum, item) => {
      return sum + (parseFloat(item.variant.price.amount) * item.quantity)
    }, 0)

    // Format total
    const formattedTotal = totalAmount.toFixed(2)

    // Create the transformed cart to match our frontend expectations
    const transformedCart = {
      id: cart.id,
      checkoutUrl: cart.checkoutUrl,
      totalPrice: {
        amount: formattedTotal,
        currencyCode: 'AUD'
      },
      subtotalPrice: {
        amount: formattedTotal,
        currencyCode: 'AUD'
      },
      lineItemsSubtotalPrice: {
        amount: formattedTotal,
        currencyCode: 'AUD'
      },
      lineItems: lineItemsData
    }

    // // console.log('Transformed cart with prices:', transformedCart)

    // Update Redux state
    dispatch(updateCartFromShopify(transformedCart))

    // Clear any previous errors since operation was successful
    dispatch(clearCartError())

    return transformedCart
  } catch (error) {
    console.error('Error processing cart response:', error)
    dispatch(setCartError({ message: 'Error processing cart data.' }))
    return null
  }
}

/**
 * Fetch the current cart’s contents and update Redux.
 * Calls GET /api/getCartItems?cartId=…
 */
export const syncShopifyCart = async (dispatch, getState) => {
  dispatch(clearCartError())
  dispatch(setCartBusy(true))

  const cartId = window.localStorage.getItem('cartId')
  if (!cartId) {
    // no cart yet — reset to empty
    dispatch(updateCartFromShopify({
      id: null,
      checkoutUrl: null,
      totalPrice: { amount: '0.00', currencyCode: 'AUD' },
      subtotalPrice: { amount: '0.00', currencyCode: 'AUD' },
      lineItemsSubtotalPrice: { amount: '0.00', currencyCode: 'AUD' },
      lineItems: []
    }))
    dispatch(setCartBusy(false))
    return null
  }

  try {
    const resp = await fetch(`https://api-server.discoveredfoods.com/api/getCartItems?cartId=${encodeURIComponent(cartId)}`)
    if (!resp.ok) {
      // **local-storage-fixes-s//
      // If cart not found (404) or other error, clear invalid cart data
      if (resp.status === 404 || resp.status === 400) {
        console.log('🧹 Cart not found on server, clearing localStorage...')
        window.localStorage.removeItem('cartId')
        window.localStorage.removeItem('cartLines')
        window.localStorage.removeItem('discoveredFoodsCart1')
        // Reset to empty cart
        dispatch(updateCartFromShopify({
          id: null,
          checkoutUrl: null,
          totalPrice: { amount: '0.00', currencyCode: 'AUD' },
          subtotalPrice: { amount: '0.00', currencyCode: 'AUD' },
          lineItemsSubtotalPrice: { amount: '0.00', currencyCode: 'AUD' },
          lineItems: []
        }))
        dispatch(setCartBusy(false))
        return null
      }
      // **local-storage-fixes-e//
      throw new Error(`Failed to load cart (${resp.status})`)
    }

    // server returns { data: { cart: { … } } }
    const payload = await resp.json()

    // processCartResponse expects a Response-like, with JSON { data: { cart: … } }
    // wrap it in a fake Response so all your parsing/transform logic stays DRY
    const fakeResponse = new Response(JSON.stringify(payload), {
      status: 200,
      headers: { 'Content-Type': 'application/json' }
    })

    const transformed = await processCartResponse(fakeResponse, dispatch)
    return transformed
  } catch (error) {
    console.error('Error fetching cart items:', error)
    // **local-storage-fixes-s//
    // If it's a network or server error related to invalid cart, clear localStorage
    const errorMsgLower = error?.message ? error.message.toLowerCase() : ''
    const isInvalidCart = errorMsgLower.includes('cart does not exist') ||
      errorMsgLower.includes('cart is invalid') ||
      errorMsgLower.includes('invalid cart') ||
      errorMsgLower.includes('cart') ||
      errorMsgLower.includes('404') ||
      errorMsgLower.includes('400')

    if (isInvalidCart) {
      console.log('🧹 Clearing potentially invalid cart data due to error...')
      window.localStorage.removeItem('cartId')
      window.localStorage.removeItem('cartLines')
      window.localStorage.removeItem('discoveredFoodsCart1')
    }
    // **local-storage-fixes-e//
    dispatch(setCartError({ message: error.message || 'Could not load cart.' }))
    return null
  } finally {
    dispatch(setCartBusy(false))
  }
}

/**
 * Add an item to the Shopify cart
 * @param {Object} params - The parameters object
 * @returns {Promise} - A promise that resolves to the updated cart data
 */
export const addItemToShopifyCart = async ({ dispatch, getState, variantId, quantity, sellingPlanId }) => {
  // Clear any previous errors
  dispatch(clearCartError())

  // Safely access the cart busy state
  let busy = false
  try {
    const state = getState()
    busy = state && state.cart && state.cart.busy
  } catch (e) {
    // // console.log('Could not access cart busy state, assuming not busy')
  }

  if (busy) {
    // // console.log('Cart is busy, deferring add item operation')
    dispatch(setCartError({ message: 'Another cart operation is in progress. Please try again.' }))
    return null
  }

  try {
    // Get the price from localStorage
    let price = null
    let productHandle = null

    try {
      // Try to get the product handle from the DOM
      productHandle = getProductHandleFromVariantId(variantId)

      if (productHandle) {
        const storedPricesJson = localStorage.getItem('shopifyProductPrices')
        if (storedPricesJson) {
          const storedPrices = JSON.parse(storedPricesJson)
          price = storedPrices[productHandle]
          // // console.log(`Found stored price for ${productHandle}: ${price}`)
        }
      }

      // If no stored price found, try to get from a BuyButton element
      if (!price && typeof document !== 'undefined') {
        const buyButtons = document.querySelectorAll('.BuyButton')
        buyButtons.forEach(button => {
          const buttonPrice = button.getAttribute('data-product-price')
          if (buttonPrice) {
            price = buttonPrice
            // // console.log(`Found price from BuyButton: ${price}`)
          }
        })
      }
    } catch (error) {
      console.error('Error getting price from localStorage:', error)
    }

    const base64VariantId = encodeVariantId(variantId)

    // Build the line item - based on server.js expected format
    const lineItem = {
      merchandiseId: base64VariantId,
      quantity: parseInt(quantity, 10)
    }

    // Add selling plan if provided (subscription)
    if (sellingPlanId) {
      lineItem.sellingPlanId = encodeSellingPlanId(sellingPlanId)
    }

    // Store price in localStorage for future reference
    try {
      if (price && productHandle) {
        const storedPricesJson = localStorage.getItem('shopifyProductPrices') || '{}'
        const storedPrices = JSON.parse(storedPricesJson)
        storedPrices[productHandle] = price
        localStorage.setItem('shopifyProductPrices', JSON.stringify(storedPrices))
        // // console.log(`Stored price for ${productHandle}: ${price}`)
      }
    } catch (error) {
      // console.error('Error storing product price:', error)
    }

    // First, ensure we have a valid cart ID from localStorage
    const cartId = window.localStorage.getItem('cartId')

    // Prepare the request data based on server.js endpoint
    const data = {
      cartId: cartId,
      lines: [lineItem]
    }

    // // console.log('Adding to cart with data:', JSON.stringify(data))

    // Use a short timeout to prevent race conditions with simultaneous requests
    await new Promise(resolve => setTimeout(resolve, 100))

    // Call the server endpoint (matches server.js implementation)
    const response = await fetch('https://api-server.discoveredfoods.com/api/addToCart', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      const errorText = await response.text()
      console.error(`HTTP error! Status: ${response.status}`, errorText)
      // **local-storage-fixes-s//

      const lowerErrorText = errorText ? errorText.toLowerCase() : ''
      const isInvalidCart = response.status === 400 &&
        (lowerErrorText.includes('cart does not exist') ||
          lowerErrorText.includes('cart is invalid') ||
          lowerErrorText.includes('invalid cart') ||
          lowerErrorText.includes('cart'))

      // If it's a 400 error (likely invalid cart), clear localStorage and retry
      if (isInvalidCart) {
        console.log('🧹 Invalid cart detected, clearing localStorage and retrying...')
        window.localStorage.removeItem('cartId')
        window.localStorage.removeItem('cartLines')
        window.localStorage.removeItem('discoveredFoodsCart1')

        // Retry the request without cartId (will create new cart)
        const retryData = {
          cartId: null,
          lines: [lineItem]
        }
        const retryResponse = await fetch('https://api-server.discoveredfoods.com/api/addToCart', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(retryData)
        })
        if (!retryResponse.ok) {
          dispatch(setCartError({ message: 'Could not add item to cart.' }))
          throw new Error(`Retry failed! Status: ${retryResponse.status}`)
        }
        const retryResponseData = await retryResponse.json()
        // Process the retry response normally
        return processCartResponse(new Response(JSON.stringify(retryResponseData), {
          status: retryResponse.status,
          statusText: retryResponse.statusText,
          headers: retryResponse.headers
        }), dispatch)
      }
      // **local-storage-fixes-e//
      dispatch(setCartError({ message: `Could not add item to cart (${response.status})` }))
      throw new Error(`HTTP error! Status: ${response.status}`)
    }

    const responseData = await response.json()

    // Check for Shopify API errors (from server.js)
    if (responseData.errors ||
      (responseData.data && responseData.data.cartLinesAdd && responseData.data.cartLinesAdd.userErrors &&
        responseData.data.cartLinesAdd.userErrors.length > 0)) {
      const errors = responseData.errors || responseData.data.cartLinesAdd.userErrors
      const errorMessage = errors.map(e => e.message).join(', ')
      console.error('Shopify API error:', errorMessage)

      // Check if this is an invalid/expired cart error
      const errorMsgLower = errorMessage.toLowerCase()
      const isInvalidCart = errorMsgLower.includes('cart does not exist') ||
        errorMsgLower.includes('cart is invalid') ||
        errorMsgLower.includes('invalid cart')

      if (isInvalidCart) {
        console.log('🧹 Invalid cart in Shopify userErrors, clearing localStorage and retrying with fresh cart...')
        window.localStorage.removeItem('cartId')
        window.localStorage.removeItem('cartLines')
        window.localStorage.removeItem('discoveredFoodsCart1')

        // Retry without a cartId — Shopify will create a new cart
        const retryData = { cartId: null, lines: [lineItem] }
        try {
          const retryResponse = await fetch('https://api-server.discoveredfoods.com/api/addToCart', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(retryData)
          })
          if (retryResponse.ok) {
            const retryResponseData = await retryResponse.json()
            return processCartResponse(new Response(JSON.stringify(retryResponseData), {
              status: retryResponse.status,
              statusText: retryResponse.statusText,
              headers: retryResponse.headers
            }), dispatch)
          }
        } catch (retryError) {
          console.error('Retry after invalid cart error failed:', retryError)
        }
      }

      dispatch(setCartError({ message: errorMessage }))
      return null
    }

    // Extract cart ID from response and save to localStorage
    if (responseData.data && responseData.data.cartLinesAdd && responseData.data.cartLinesAdd.cart) {
      const newCartId = responseData.data.cartLinesAdd.cart.id
      window.localStorage.setItem('cartId', newCartId)
      // // console.log('Saved new cart ID to localStorage:', newCartId)
    } else if (responseData.data && responseData.data.cartCreate && responseData.data.cartCreate.cart) {
      const newCartId = responseData.data.cartCreate.cart.id
      window.localStorage.setItem('cartId', newCartId)
      // // console.log('Saved new cart ID to localStorage:', newCartId)
    }

    // Update stored cart lines to track selling plan associations
    try {
      if (responseData.data && (responseData.data.cartLinesAdd?.cart?.lines?.edges || responseData.data.cartCreate?.cart?.lines?.edges)) {
        const edges = responseData.data.cartLinesAdd?.cart?.lines?.edges || responseData.data.cartCreate?.cart?.lines?.edges || []
        const existingCartLines = JSON.parse(window.localStorage.getItem('cartLines') || '[]')

        // Find the new line item based on cart response
        const newLineItems = edges
          .filter(edge => {
            const merchandise = edge.node.merchandise
            if (!merchandise) return false

            // Try to match the variant ID
            const responseVariantId = merchandise.id?.split('/').pop()
            const requestVariantId = variantId.toString().split('/').pop()
            return responseVariantId === requestVariantId
          })
          .map(edge => ({
            lineItemId: edge.node.id,
            variantId: variantId.toString(),
            sellingPlanId: sellingPlanId || null,
            price: price
          }))

        // Add any new line items that don't already exist
        newLineItems.forEach(newLine => {
          if (!existingCartLines.some(line => line.lineItemId === newLine.lineItemId)) {
            existingCartLines.push(newLine)
          }
        })

        window.localStorage.setItem('cartLines', JSON.stringify(existingCartLines))
      }
    } catch (error) {
      console.error('Error updating localStorage cart lines:', error)
    }

    // Return the processed cart data
    return processCartResponse(new Response(JSON.stringify(responseData), {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers
    }), dispatch)
  } catch (error) {
    console.error('Error adding item to Shopify cart:', error)
    dispatch(setCartError({
      message: error.message || 'Failed to add item to cart. Please try again.'
    }))
    return null
  }
}

/**
 * Remove an item from the Shopify cart
 * This improved version ensures the loading state is always properly reset
 * and works with the server.js implementation
 *
 * @param {Object} params - The parameters object
 * @param {function} params.dispatch - The Redux dispatch function
 * @param {function} params.getState - The Redux getState function
 * @param {string} params.lineItemId - The line item ID to remove
 * @returns {Promise} - A promise that resolves to the updated cart data
 */
export const removeItemFromShopifyCart = async ({ dispatch, getState, lineItemId }) => {
  // Clear any previous errors
  dispatch(clearCartError())

  try {
    // Check if we have a cart ID
    const cartId = window.localStorage.getItem('cartId')
    if (!cartId) {
      // // console.log('No cart ID found in localStorage')
      dispatch(setCartError({ message: 'Cart ID not found. Please refresh the page.' }))

      // Important: Update the cart state even if we couldn't remove the item
      dispatch(updateCartFromShopify(null))
      return Promise.resolve(null)
    }

    if (!lineItemId) {
      // console.error('lineItemId is required but was not provided')
      dispatch(setCartError({ message: 'Invalid cart item.' }))

      // Reset loading state
      const currentCart = getState()?.cart?.content || null
      dispatch(updateCartFromShopify(currentCart))
      return Promise.resolve(null)
    }

    // // console.log(`Removing line item ${lineItemId} from cart ${cartId}`)

    // Prepare request data according to server.js implementation
    // The server.js expects { cartId, lineId } format
    const data = {
      cartId: cartId,
      lineId: lineItemId
    }

    // // console.log('Sending remove cart request with payload:', JSON.stringify(data, null, 2))

    // Call the removeFromCart endpoint from server.js
    const response = await fetch('https://api-server.discoveredfoods.com/api/removeFromCart', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })

    if (!response.ok) {
      // console.error(`Remove cart API returned error status: ${response.status}`)
      try {
        const errorText = await response.text()
        console.error('Error response:', errorText)
        dispatch(setCartError({ message: 'Could not remove item from cart.' }))
      } catch (e) {
        console.error('Unable to get error response text')
        dispatch(setCartError({ message: 'Could not remove item from cart.' }))
      }

      // Important: Still try to sync the cart even if the API call failed
      await syncShopifyCart(dispatch, getState)
      return null
    }

    // Process successful response
    const responseData = await response.json()

    // Check for user errors in the Shopify response (server.js specific)
    if (responseData.data &&
      responseData.data.cartLinesRemove &&
      responseData.data.cartLinesRemove.userErrors &&
      responseData.data.cartLinesRemove.userErrors.length > 0) {
      const errors = responseData.data.cartLinesRemove.userErrors
      const errorMessage = errors.map(e => e.message).join(', ')
      console.error('Shopify API error during removal:', errorMessage)
      dispatch(setCartError({ message: errorMessage }))

      // Sync cart after error
      await syncShopifyCart(dispatch, getState)
      return null
    }

    // Update cart lines in localStorage to remove the item
    try {
      const existingCartLines = JSON.parse(window.localStorage.getItem('cartLines') || '[]')
      const updatedCartLines = existingCartLines.filter(line => line.lineItemId !== lineItemId)
      window.localStorage.setItem('cartLines', JSON.stringify(updatedCartLines))
    } catch (error) {
      console.error('Error updating localStorage cart lines:', error)
    }

    return await syncShopifyCart(dispatch, getState)
  } catch (error) {
    console.error('Error removing item from Shopify cart:', error)

    // Set error in Redux
    dispatch(setCartError({
      message: error.message || 'Error removing item from cart. Please try again.'
    }))

    // Critical: Still sync the cart even if there was an error
    // This ensures the loading state is cleared
    // // console.log('Error encountered, syncing cart to ensure UI consistency')
    try {
      await syncShopifyCart(dispatch, getState)
    } catch (syncError) {
      console.error('Failed to sync cart after removal error:', syncError)
      // As a last resort, reset the cart loading state
      const currentCart = getState()?.cart?.content || null
      dispatch(updateCartFromShopify(currentCart))
    }

    return null
  }
}

/**
 * Update the quantity of an item in the Shopify cart
 * @param {Object} params - The parameters object
 * @param {function} params.dispatch - The Redux dispatch function
 * @param {function} params.getState - The Redux getState function
 * @param {string} params.lineItemId - The line item ID to update
 * @param {number} params.quantity - The new quantity
 * @returns {Promise} - A promise that resolves to the updated cart data
 */
// In your front-end API.js file, update this function:
export const updateShopifyCartItemQuantity = async ({ dispatch, getState, lineItemId, quantity }) => {
  dispatch(clearCartError())
  dispatch(setCartBusy(true)) // Make sure to set cart busy state

  const cartId = window.localStorage.getItem('cartId')
  if (!cartId) {
    dispatch(setCartError({ message: 'Cart ID not found. Please refresh the page.' }))
    dispatch(setCartBusy(false))
    return null
  }

  if (!lineItemId) {
    console.error('Line item ID is required for updating quantity')
    dispatch(setCartError({ message: 'Invalid cart item.' }))
    dispatch(setCartBusy(false))
    return null
  }

  const parsedQuantity = parseInt(quantity, 10)
  if (isNaN(parsedQuantity) || parsedQuantity < 1) {
    console.error('Invalid quantity value:', quantity)
    dispatch(setCartError({ message: 'Invalid quantity value.' }))
    dispatch(setCartBusy(false))
    return null
  }

  // console.log(`Updating cart item: ${lineItemId} to quantity ${parsedQuantity}`)

  const data = {
    cartId,
    lines: [{ id: lineItemId, quantity: parsedQuantity }]
  }

  try {
    // console.log('Sending update request:', data)

    const response = await fetch('https://api-server.discoveredfoods.com/api/updateCartItem', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data)
    })

    dispatch(setCartBusy(false))

    if (!response.ok) {
      const errorText = await response.text()
      console.error(`HTTP error! Status: ${response.status}`, errorText)
      dispatch(setCartError({ message: 'Could not update cart item quantity.' }))
      dispatch(setCartBusy(false))
      return null
    }

    const responseData = await response.json()
    // console.log('Update response:', responseData)

    // After successful update, sync the cart again to ensure everything is up to date
    await syncShopifyCart(dispatch, getState)

    return responseData
  } catch (error) {
    console.error('Error updating item quantity in Shopify cart:', error)
    dispatch(setCartError({ message: error.message || 'Error updating cart. Please try again.' }))

    // Attempt to sync cart after error to ensure UI is consistent
    try {
      await syncShopifyCart(dispatch, getState)
    } catch (syncError) {
      console.error('Failed to sync cart after quantity update error:', syncError)
    }

    return null
  } finally {
    dispatch(setCartBusy(false))
  }
}

export { clearCartError } from './slices/cart'
